import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import { ConfilexMark, ConfilexWordmark } from "@/components/ConfilexLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [emailHint, setEmailHint] = useState(null);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      if (!token) {
        setChecking(false);
        setValid(false);
        return;
      }
      try {
        const r = await axios.get(`${API}/auth/reset-password/verify`, { params: { token } });
        if (!cancelled) {
          setValid(!!r.data?.valid);
          setEmailHint(r.data?.email_hint || null);
        }
      } catch {
        if (!cancelled) setValid(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    verify();
    return () => { cancelled = true; };
  }, [token]);

  const policyChecks = [
    { label: "Au moins 10 caractères", ok: pwd.length >= 10 },
    { label: "Une majuscule", ok: /[A-Z]/.test(pwd) },
    { label: "Un chiffre", ok: /\d/.test(pwd) },
    { label: "Les deux saisies correspondent", ok: pwd.length > 0 && pwd === pwd2 },
  ];
  const allOk = policyChecks.every((c) => c.ok);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!allOk) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password: pwd });
      setDone(true);
      toast.success("Mot de passe mis à jour");
      setTimeout(() => nav("/login"), 2000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Une erreur est survenue. Demandez un nouveau lien.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B3A2A] text-white flex items-center justify-center p-6 grain relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <img src="/art/sorola/promenade-1.jpg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B3A2A]/60 to-[#0B3A2A]"></div>
      </div>
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10" data-testid="back-home">
          <ConfilexMark size={40} theme="light" />
          <ConfilexWordmark size={22} theme="light" />
        </Link>

        <div className="glass-dark rounded-2xl p-8 fade-up" data-testid="reset-password-card">
          {checking ? (
            <div className="text-center py-10" data-testid="reset-checking">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#C9A961]" />
              <p className="text-white/60 text-sm mt-3">Vérification du lien…</p>
            </div>
          ) : !valid ? (
            <div className="text-center" data-testid="reset-invalid">
              <div className="mx-auto w-14 h-14 rounded-full bg-rose-500/15 border border-rose-400/40 flex items-center justify-center mb-4">
                <AlertTriangle className="w-7 h-7 text-rose-300" />
              </div>
              <h1 className="font-serif text-2xl mb-2">Lien invalide ou expiré</h1>
              <p className="text-white/70 text-sm">
                Ce lien n'est plus valide. Cela peut arriver s'il a été utilisé, s'il a expiré (après 60 minutes), ou s'il a été modifié.
              </p>
              <Link to="/forgot-password" className="inline-block mt-6 text-[#C9A961] hover:underline text-sm" data-testid="request-new-link">
                Demander un nouveau lien
              </Link>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center mb-4">
                <ShieldCheck className="w-7 h-7 text-emerald-300" />
              </div>
              <h1 className="font-serif text-2xl mb-2">Mot de passe mis à jour</h1>
              <p className="text-white/70 text-sm">Vous allez être redirigé vers la connexion…</p>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-3xl mb-2">Nouveau mot de passe</h1>
              <p className="text-white/60 text-sm mb-6">
                Compte : <span className="text-[#C9A961]">{emailHint || "—"}</span>
              </p>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="pwd" className="text-white/80 mb-2 block">Nouveau mot de passe</Label>
                  <Input
                    id="pwd"
                    type="password"
                    required
                    autoFocus
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    data-testid="reset-password-input"
                    className="bg-white/5 border-white/10 text-white rounded-xl h-11 focus:border-[#C9A961]"
                  />
                </div>
                <div>
                  <Label htmlFor="pwd2" className="text-white/80 mb-2 block">Confirmer le mot de passe</Label>
                  <Input
                    id="pwd2"
                    type="password"
                    required
                    value={pwd2}
                    onChange={(e) => setPwd2(e.target.value)}
                    data-testid="reset-password-confirm"
                    className="bg-white/5 border-white/10 text-white rounded-xl h-11 focus:border-[#C9A961]"
                  />
                </div>

                <ul className="text-xs space-y-1 bg-white/5 rounded-lg p-3 border border-white/10">
                  {policyChecks.map((c) => (
                    <li key={c.label} className={c.ok ? "text-emerald-300" : "text-white/50"}>
                      {c.ok ? "✓" : "○"} {c.label}
                    </li>
                  ))}
                </ul>

                <Button
                  type="submit"
                  disabled={!allOk || loading}
                  data-testid="reset-password-submit"
                  className="w-full h-11 rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold disabled:opacity-50">
                  {loading ? "Mise à jour…" : "Définir le nouveau mot de passe"}
                </Button>
              </form>
            </>
          )}

          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <Link to="/login" className="text-[#C9A961] hover:underline text-sm inline-flex items-center gap-1" data-testid="back-to-login">
              <ArrowLeft className="w-4 h-4" /> Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
